import { TextareaHTMLAttributes, useEffect, useRef } from 'react';
import { UseFormRegister, FieldValues, Path } from 'react-hook-form';

interface TextareaProps<T extends FieldValues> extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'name'> {
  name: Path<T>;
  label: string;
  rows?: number;
  error?: string;
  register: UseFormRegister<T>;
  autoResize?: boolean;
}

export const Textarea = <T extends FieldValues>({
  name,
  label,
  rows = 4,
  error,
  register,
  placeholder,
  className = '',
  autoResize = false,
  ...props
}: TextareaProps<T>) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const { ref, ...registerProps } = register(name);

  // Auto-resize functionality
  useEffect(() => {
    if (autoResize && textareaRef.current) {
      const textarea = textareaRef.current;
      const adjustHeight = () => {
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
      };

      textarea.addEventListener('input', adjustHeight);
      adjustHeight(); // Initial adjustment

      return () => textarea.removeEventListener('input', adjustHeight);
    }
  }, [autoResize]);

  const baseClasses = 'block w-full px-4 py-3 text-gray-900 border-3 font-body transition-all duration-200 focus:outline-none focus:border-4 focus:-translate-y-1 focus:shadow-[4px_4px_0px_0px_rgba(0,60,102,0.3)] disabled:opacity-50 disabled:cursor-not-allowed resize-y bg-white';

  const stateClasses = error
    ? 'border-amaranth_purple-500 focus:border-amaranth_purple-500'
    : 'border-gray-400 focus:border-indigo_dye-500 hover:bg-gray-50';

  const resizeClass = autoResize ? 'resize-none' : 'resize-y';

  return (
    <div className="w-full">
      <label
        htmlFor={name}
        className="block font-mono text-sm uppercase tracking-wide font-semibold text-gray-900 mb-2"
      >
        {label}
      </label>
      <textarea
        id={name}
        rows={rows}
        placeholder={placeholder}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={error ? `${name}-error` : undefined}
        className={`${baseClasses} ${stateClasses} ${resizeClass} ${className}`}
        {...registerProps}
        ref={(e) => {
          ref(e);
          textareaRef.current = e;
        }}
        {...props}
      />
      {error && (
        <p
          id={`${name}-error`}
          className="mt-2 font-mono text-xs uppercase tracking-wide font-bold text-amaranth_purple-600"
          role="alert"
        >
          {error}
        </p>
      )}
    </div>
  );
};
